"use client";

import React, { useMemo, useState, useEffect } from "react";
import { Users, Edit3Icon, Plus, Trash2, X } from "lucide-react";
import { createPortal } from "react-dom";
import { useSuppliers } from "../context/SuppliersContext";
import { useStates } from "../context/StatesContext";
import { useDistricts } from "../context/DistrictsContext";
import { useMandals } from "../context/MandalsContext";
import { useCities } from "../context/CitiesContext";
import { useSupplierTypes } from "../context/SupplierTypesContext";

/* ─── Reusable primitives ────────────────────────────────────── */

const Section = ({ title, children }) => (
  <div className="bg-[#e6d5c3] rounded-2xl p-4 sm:p-5 space-y-3 sm:space-y-4">
    <p className="text-sm font-semibold text-gray-700">{title}</p>
    {children}
  </div>
);

const Input = ({ label, value, onChange, type = "text", error }) => (
  <div>
    <label className="text-xs sm:text-sm flex items-center gap-2 mb-1">
      <span className="w-2 h-2 bg-orange-500 rounded-full flex-shrink-0" />
      {label}
    </label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl border outline-none bg-white text-sm sm:text-base ${
        error ? "border-red-400 bg-red-50" : "border-orange-200"
      }`}
    />
    {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
  </div>
);

const FormSelect = ({ label, value, onChange, options, disabled = false }) => (
  <div>
    <label className="text-xs sm:text-sm flex items-center gap-2 mb-1">
      <span className="w-2 h-2 bg-orange-500 rounded-full flex-shrink-0" />
      {label}
    </label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl border border-orange-200 bg-white text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <option value="">Select</option>
      {options.map((o) => (
        <option key={String(o.value)} value={String(o.value)}>
          {o.label}
        </option>
      ))}
    </select>
  </div>
);

const Toggle = ({ checked, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    style={{ backgroundColor: checked ? "#16a34a" : "#d1d5db" }}
    className="relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors duration-200"
  >
    <span
      style={{ transform: checked ? "translateX(1.375rem)" : "translateX(0.25rem)" }}
      className="inline-block h-4 w-4 rounded-full bg-white shadow transition-transform duration-200"
    />
  </button>
);

const StatusBtn = ({ value, current, set }) => {
  const isSelected = current === value;
  const style = isSelected
    ? value === "active"
      ? { backgroundColor: "#dcfce7", color: "#15803d" }
      : { backgroundColor: "#fee2e2", color: "#b91c1c" }
    : { backgroundColor: "#f3f4f6", color: "#6b7280" };
  return (
    <button
      type="button"
      onClick={() => set(value)}
      style={style}
      className="px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm capitalize font-medium"
    >
      {value}
    </button>
  );
};

const Stat = ({ label, value, color = "text-gray-900" }) => (
  <div className="bg-white border border-orange-100 rounded-xl p-3 sm:p-4">
    <p className="text-xs text-gray-500">{label}</p>
    <p className={`text-xl sm:text-2xl font-bold ${color}`}>{value}</p>
  </div>
);

const Actions = ({ item, onEdit, onDelete }) => (
  <div className="flex items-center gap-2 sm:gap-3">
    <button
      type="button"
      onClick={onEdit}
      className="text-teal-600 hover:text-teal-800 p-1"
    >
      <Edit3Icon size={16} />
    </button>
    <button
      type="button"
      onClick={() => onDelete(item.id)}
      className="text-red-500 hover:text-red-700 p-1"
    >
      <Trash2 size={16} />
    </button>
  </div>
);

/* ─── FIX: helpers to resolve supplier type fields safely ─────── */

// Tries all possible API field names for the display name
const resolveSupplierTypeName = (item) =>
  item.customer_type_name ||
  item.supplier_type_name ||
  item.customerTypeName ||
  item.supplierTypeName ||
  item.customer_type ||
  item.supplier_type ||
  null;

// Tries all possible API field names for the ID, always returns a String
const resolveSupplierTypeId = (item) => {
  const raw =
    item.customer_type_id ??
    item.customerTypeId ??
    item.supplier_type_id ??
    item.supplierTypeId ??
    "";
  return String(raw);
};

/* ─── Blank form ─────────────────────────────────────────────── */

const BLANK = {
  firstName: "",
  lastName: "",
  phone: "",
  address: "",
  creditLimit: "",
  email: "",
  gender: "",
  pincode: "",
  customerType: "",
  outstanding: "",
  status: "active",
  stateId: "",
  districtId: "",
  mandalId: "",
  cityId: "",
};

/* ─── Page ───────────────────────────────────────────────────── */

export default function SuppliersPage() {
  const {
    suppliers,
    loading,
    addSupplier,
    updateSupplier,
    deleteSupplier,
    toggleSupplierStatus,
  } = useSuppliers();

  const { states, fetchStates } = useStates();
  const { districts, fetchDistricts } = useDistricts();
  const { mandals, fetchMandals } = useMandals();
  const { cities, fetchCities } = useCities();
  const { supplierTypes, fetchSupplierTypes } = useSupplierTypes();

  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [formLoading, setFormLoading] = useState(false);
const [errors, setErrors] = useState({});
  // Fetch initial data
  useEffect(() => {
    fetchStates();
    fetchSupplierTypes();
  }, []);

  const set = (key) => (val) => setForm((f) => ({ ...f, [key]: val }));

  /* ── Status toggle ──────────────────────────────────────────── */
  const handleToggleStatus = async (item) => {
    const newStatus = item.status === "active" ? "inactive" : "active";
    try {
      if (typeof toggleSupplierStatus === "function") {
        await toggleSupplierStatus(item.id, item.status);
      } else {
        await updateSupplier(item.id, { ...item, status: newStatus });
      }
    } catch (err) {
      console.error("Toggle status error:", err);
      alert(err.message || "Could not update status");
    }
  };

  /* ── Location cascade handlers ────────────────────────────── */
const handleStateChange = async (val) => {
  setForm((f) => ({ ...f, stateId: val, districtId: "", mandalId: "", cityId: "" }));
  if (val) await fetchDistricts(val);
};

const handleDistrictChange = async (val) => {
  setForm((f) => ({ ...f, districtId: val, mandalId: "", cityId: "" }));
  if (val) {
    await fetchMandals(val);
  }
};

const handleMandalChange = async (val) => {
  setForm((f) => ({ ...f, mandalId: val, cityId: "" }));
  if (val) {
    await fetchCities(val);
  }
};

  /* ── Open Add ──────────────────────────────────────────────── */
  const openAdd = () => {
    setEditing(null);
    setForm(BLANK);
    setModalOpen(true);
  };

  /* ── Open Edit ─────────────────────────────────────────────── */
const openEdit = async (item) => {
  setFormLoading(true);
  setModalOpen(true);
  setEditing(item);

  try {
    await Promise.all([fetchSupplierTypes(), fetchStates()]);

    // 👇 ADD THESE TWO LINES
    console.log("Supplier item:", item);
    console.log("Supplier types loaded:", supplierTypes);
    console.log("Resolved type ID:", resolveSupplierTypeId(item));
    // ...rest of your code

    const stateId    = String(item.state_id    || item.stateId    || item.state    || "");
    const districtId = String(item.district_id || item.districtId || item.district || "");
    const mandalId   = String(item.mandal_id   || item.mandalId   || item.mandal   || "");
    const cityId     = String(item.city_id     || item.cityId     || item.city     || "");

    const locationFetches = [];
    if (stateId)    locationFetches.push(fetchDistricts(stateId));
    if (districtId) locationFetches.push(fetchMandals(districtId));
    if (mandalId)   locationFetches.push(fetchCities(mandalId));
    await Promise.all(locationFetches);

    setForm({
      ...BLANK,
      firstName:    item.first_name  || "",
      lastName:     item.last_name   || "",
      phone:        item.phone       || "",
      address:      item.address     || "",
      creditLimit:  item.credit_limit || "",
      email:        item.email       || "",
      gender:       item.gender      || "",
      pincode:      item.pincode     || "",
      customerType: resolveSupplierTypeId(item), // String ID
      outstanding:  item.outstanding || "",
      status:       item.status      || "active",
      stateId,
      districtId,
      mandalId,
      cityId,
    });
  } catch (err) {
    console.error("Error loading edit form:", err);
    alert("Error loading supplier data");
  } finally {
    setFormLoading(false);
  }
};

  /* ── Save ──────────────────────────────────────────────────── */
 const save = async () => {
  const newErrors = {};

  if (!form.firstName.trim()) {
    newErrors.firstName = "First name is required.";
  }

  // Indian phone: exactly 10 digits, starts with 6–9
  const phoneRegex = /^[6-9]\d{9}$/;
  if (!form.phone.trim()) {
    newErrors.phone = "Phone number is required.";
  } else if (!phoneRegex.test(form.phone.trim())) {
    newErrors.phone = "Enter a valid 10-digit Indian mobile number (starts with 6–9).";
  }

  // Email: optional but validated if filled
  if (form.email.trim()) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email.trim())) {
      newErrors.email = "Enter a valid email address.";
    }
  }

  if (Object.keys(newErrors).length > 0) {
    setErrors(newErrors);
    return;
  }

  setErrors({});

  const payload = {
    first_name: form.firstName,
    last_name: form.lastName,
    phone: form.phone,
    address: form.address,
    credit_limit: form.creditLimit,
    email: form.email,
    gender: form.gender,
    pincode: form.pincode,
    customer_type_id: form.customerType || null,
    customer_type: form.customerType || null,
    outstanding: form.outstanding,
    status: form.status,
    state: form.stateId,
    district: form.districtId,
    mandal: form.mandalId,
    city: form.cityId,
  };

  try {
    if (editing) {
      await updateSupplier(editing.id, payload);
    } else {
      await addSupplier(payload);
    }
    setModalOpen(false);
    setEditing(null);
    setForm(BLANK);
  } catch (err) {
    console.error("Save error:", err);
    alert(err.message || "Something went wrong");
  }
};

  /* ── Delete ────────────────────────────────────────────────── */
  const handleDelete = (id) => {
    if (window.confirm("Delete this supplier?")) deleteSupplier(id);
  };

  /* ── Filter ────────────────────────────────────────────────── */
  const filtered = useMemo(
    () =>
      suppliers.filter((s) =>
        `${s.first_name} ${s.last_name}`
          .toLowerCase()
          .includes(search.toLowerCase())
      ),
    [suppliers, search]
  );

  /* ─── Render ─────────────────────────────────────────────── */
  return (
    <div className="p-3 sm:p-4 md:p-6 space-y-4 sm:space-y-6 max-w-full overflow-x-hidden">

      {/* HEADER */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
        <div className="flex items-center gap-3 sm:gap-4">
          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-teal-700 rounded-xl flex items-center justify-center shadow-sm flex-shrink-0">
            <Users className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-teal-800">
              Suppliers
            </h1>
            <p className="text-sm text-gray-600">Manage your supplier entries</p>
          </div>
        </div>
        <button
          type="button"
          onClick={openAdd}
          className="w-full sm:w-auto flex items-center justify-center gap-2 bg-teal-600 text-white px-4 sm:px-5 py-2.5 rounded-xl hover:bg-teal-700 transition text-sm sm:text-base"
        >
          <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
          Add Supplier
        </button>
      </div>

      {/* STATS */}
      <div className="grid grid-cols-3 gap-2 sm:gap-4">
        <Stat label="Total"    value={suppliers.length} />
        <Stat label="Active"   value={suppliers.filter((s) => s.status === "active").length}   color="text-green-600" />
        <Stat label="Inactive" value={suppliers.filter((s) => s.status === "inactive").length} color="text-red-600" />
      </div>

      {/* SEARCH */}
      <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 sm:p-4">
        <input
          placeholder="Search supplier..."
          className="px-3 sm:px-4 py-2 border rounded-xl w-full text-sm sm:text-base"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* TABLE — desktop/tablet (md+) — DESKTOP UNCHANGED */}
    <div className="hidden [@media(min-width:900px)]:block overflow-x-auto bg-white rounded-xl shadow-sm border">
        <table className="min-w-full text-sm text-left">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              {[
                "Name",
                "Supplier Type",
                "Phone",
                "Address",
                "Credit Limit",
                "Outstanding",
                "Status",
                "",
              ].map((h) => (
                <th key={h} className="px-4 lg:px-6 py-3 font-medium whitespace-nowrap">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8} className="text-center py-8 text-gray-400">
                  Loading...
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-center py-8 text-gray-400">
                  No suppliers found
                </td>
              </tr>
            ) : (
              filtered.map((item) => (
                <tr
                  key={item.id}
                  className="border-t hover:bg-gray-50 transition-colors"
                >
                  <td className="px-4 lg:px-6 py-4 font-medium whitespace-nowrap">
                    {item.first_name} {item.last_name}
                  </td>
                  {/* FIX: use helper so all possible API field names are checked */}
                  <td className="px-4 lg:px-6 py-4 text-gray-600">
                    {resolveSupplierTypeName(item) ?? "-"}
                  </td>
                  <td className="px-4 lg:px-6 py-4 text-gray-600">{item.phone}</td>
                  <td className="px-4 lg:px-6 py-4 text-gray-600 max-w-[150px] lg:max-w-[180px] truncate">
                    {item.address || "-"}
                  </td>
                  <td className="px-4 lg:px-6 py-4 text-gray-600 whitespace-nowrap">
                    ₹ {item.credit_limit || 0}
                  </td>
                  <td className="px-4 lg:px-6 py-4 text-gray-600 whitespace-nowrap">
                    ₹ {item.outstanding || 0}
                  </td>
                  <td className="px-4 lg:px-6 py-4">
                    <Toggle
                      checked={item.status === "active"}
                      onToggle={() => handleToggleStatus(item)}
                    />
                  </td>
                  <td className="px-4 lg:px-6 py-4">
                    <div className="flex justify-end">
                      <Actions
                        item={item}
                        onEdit={() => openEdit(item)}
                        onDelete={handleDelete}
                      />
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* CARDS — mobile only (below md) */}
    <div className="[@media(min-width:900px)]:hidden space-y-3">
        {loading ? (
          <div className="text-center py-8 text-gray-400">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-8 text-gray-400">No suppliers found</div>
        ) : (
          filtered.map((item) => (
            <div
              key={item.id}
              className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 space-y-2"
            >
              <div className="flex justify-between items-start gap-2">
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-sm truncate">
                    {item.first_name} {item.last_name}
                  </p>
                  {/* FIX: same helper used in mobile cards */}
                  <p className="text-xs text-gray-500 mt-0.5">
                    {resolveSupplierTypeName(item) ?? "No type"}
                  </p>
                </div>
                <Actions
                  item={item}
                  onEdit={() => openEdit(item)}
                  onDelete={handleDelete}
                />
              </div>

              <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-gray-500">
                <p>📞 {item.phone}</p>
                <p className="text-right truncate">{item.address || "-"}</p>
                <p>Credit: ₹{item.credit_limit || 0}</p>
                <p className="text-right">Due: ₹{item.outstanding || 0}</p>
              </div>

              <div className="flex justify-between items-center pt-1 border-t border-gray-100">
                <span className="text-xs text-gray-500">Status</span>
                <Toggle
                  checked={item.status === "active"}
                  onToggle={() => handleToggleStatus(item)}
                />
              </div>
            </div>
          ))
        )}
      </div>

      {/* MODAL */}
      {modalOpen &&
        createPortal(
          <div className="fixed inset-0 z-50 flex justify-center sm:justify-end">
            <div
              className="absolute inset-0 bg-black/60"
              onClick={() => setModalOpen(false)}
            />
            <div
              className="relative w-full sm:max-w-md h-full bg-[#f3e7db] sm:rounded-l-3xl shadow-2xl overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="sticky top-0 z-10 bg-[#f3e7db] p-4 sm:p-6 border-b border-orange-200">
                <div className="relative text-center">
                  <button
                    type="button"
                    onClick={() => setModalOpen(false)}
                    className="absolute top-0 left-0 w-8 h-8 sm:w-9 sm:h-9 bg-black text-white rounded-full flex items-center justify-center"
                  >
                    <X size={14} className="sm:hidden" />
                    <X size={16} className="hidden sm:block" />
                  </button>
                  <h2 className="text-lg sm:text-2xl font-bold text-teal-700">
                    {editing ? "Edit Supplier" : "Add New Supplier"}
                  </h2>
                  <div className="w-12 h-[3px] bg-orange-400 mx-auto mt-2 rounded-full" />
                </div>
              </div>

              {/* Form */}
              <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
                {formLoading ? (
                  <div className="text-center py-8 text-gray-500">
                    Loading supplier data...
                  </div>
                ) : (
                  <>
                    <Section title="Basic Info">
                      <Input
                        label="First Name *"
                        value={form.firstName}
                        onChange={set("firstName")}
                      />
                      <Input
                        label="Last Name"
                        value={form.lastName}
                        onChange={set("lastName")}
                      />
       
<FormSelect
  label="Supplier Type"
  value={form.customerType}
  onChange={set("customerType")}
  options={supplierTypes
    .filter(
      (t) =>
        t.status === "active" ||
        String(t.id) === String(form.customerType) // ← always include current
    )
    .map((t) => ({
      value: String(t.id),
      label: t.type || t.name || t.supplier_type || "",
    }))}
/>
                    </Section>

                    <Section title="Contact">
                      <Input
                        label="Email"
                        type="email"
                        value={form.email}
                        onChange={set("email")}
                      />
                      <Input
                        label="Phone *"
                        value={form.phone}
                        onChange={set("phone")}
                      />
                      <FormSelect
                        label="Gender"
                        value={form.gender}
                        onChange={set("gender")}
                        options={[
                          { value: "male", label: "Male" },
                          { value: "female", label: "Female" },
                        ]}
                      />
                    </Section>

                    <Section title="Location">
                      <FormSelect
                        label="State"
                        value={form.stateId}
                        onChange={handleStateChange}
                        options={states.map((s) => ({
                          value: String(s.id),
                          label: s.name,
                        }))}
                      />
                 
<FormSelect
  label="District"
  value={form.districtId}
  onChange={handleDistrictChange}
  options={districts
    .filter((d) => String(d.state_id || d.stateId || "") === String(form.stateId))
    .map((d) => ({ value: String(d.id), label: d.name }))}
  disabled={!form.stateId}
/>
<FormSelect
  label="Mandal"
  value={form.mandalId}
  onChange={handleMandalChange}
  options={mandals
    .filter((m) => String(m.district_id || m.districtId || "") === String(form.districtId))
    .map((m) => ({ value: String(m.id), label: m.name }))}
  disabled={!form.districtId}
/>
<FormSelect
  label="Area"
  value={form.cityId}
  onChange={set("cityId")}
  options={cities
    .filter((c) => String(c.mandal_id || c.mandalId || "") === String(form.mandalId))
    .map((c) => ({ value: String(c.id), label: c.name }))}
  disabled={!form.mandalId}
/>
                      <Input
                        label="Pincode"
                        value={form.pincode}
                        onChange={set("pincode")}
                      />
                    </Section>

                    <Section title="Address">
                      <textarea
                        value={form.address}
                        onChange={(e) => set("address")(e.target.value)}
                        placeholder="Enter address..."
                        rows={3}
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl border border-orange-200 outline-none bg-white resize-none text-sm sm:text-base"
                      />
                    </Section>

                    <Section title="Finance">
                      <Input
                        label="Credit Limit"
                        value={form.creditLimit}
                        onChange={set("creditLimit")}
                      />
                      <Input
                        label="Outstanding"
                        value={form.outstanding}
                        onChange={set("outstanding")}
                      />
                    </Section>

                    <Section title="Status">
                      <div className="flex gap-2 sm:gap-3">
                        <StatusBtn
                          value="active"
                          current={form.status}
                          set={set("status")}
                        />
                        <StatusBtn
                          value="inactive"
                          current={form.status}
                          set={set("status")}
                        />
                      </div>
                    </Section>

                    <div className="flex gap-3 sm:gap-4 pt-2 pb-4">
                      <button
                        type="button"
                        onClick={() => setModalOpen(false)}
                        className="flex-1 py-2.5 sm:py-3 rounded-xl border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 transition text-sm sm:text-base"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={save}
                        className="flex-1 py-2.5 sm:py-3 rounded-xl bg-teal-600 text-white font-semibold hover:bg-teal-700 transition text-sm sm:text-base"
                      >
                        Save
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}